import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faCopy, faSquarePlus, faSquareXmark} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {validateAllFormFields} from '../../../common/utils';
import {ApiService} from '../../services/api.service';
import {CopyButtonComponent} from '../../shared/copy-button/copy-button.component';

interface BridgeTypeOption {
    value: string;
    label: string;
    max_lpr_supported: number;
    price: number;
}

@Component({
    selector: 'app-add-bridge',
    imports: [CommonModule, FontAwesomeModule, ReactiveFormsModule, FormsModule, CopyButtonComponent],
    templateUrl: './add-bridge.component.html',
    styleUrl: './add-bridge.component.css'
})
export class AddBridgeComponent implements OnInit {
    bridgeForm!: FormGroup;
    organizationInfo: { orgId: string; orgName: string } = {orgId: '', orgName: ''};
    bridgeTypes: BridgeTypeOption[] = [];
    showSuccessModal = false;
    generatedRawKey = '';

    constructor(
        private apiService: ApiService,
        library: FaIconLibrary,
        private router: Router,
        private toastr: ToastrService,
        private fb: FormBuilder,
        private route: ActivatedRoute
    ) {
        library.addIcons(faSquarePlus, faSquareXmark, faCopy);
    }

    ngOnInit() {
        this.route.queryParams.subscribe(params => {
            this.organizationInfo['orgId'] = params['id'];
            this.organizationInfo['orgName'] = params['name'];
        });

        this.bridgeForm = this.fb.group({
            bridgeName: ['', Validators.required],
            bridgeEsn: ['', Validators.required],
            serialNumber: ['', Validators.required],
            bridgeType: ['', Validators.required],
            organization: [{value: this.organizationInfo['orgName'], disabled: true}, Validators.required]
        });

        this.loadBridgeTypes();
    }

    loadBridgeTypes() {
        this.apiService.getBridgeTypes().subscribe({
            next: (types: BridgeTypeOption[]) => {
                this.bridgeTypes = types;
            },
            error: () => {
                this.toastr.error('Unable to load bridge types.', 'Error');
            }
        });
    }

    onEsnInput(event: Event) {
        const input = event.target as HTMLInputElement;
        const upper = input.value.toUpperCase();
        if (upper !== input.value) {
            this.bridgeForm.get('bridgeEsn')?.setValue(upper, {emitEvent: false});
        }
    }

    onSerialNumberInput(event: Event) {
        const input = event.target as HTMLInputElement;
        const upper = input.value.toUpperCase();
        if (upper !== input.value) {
            this.bridgeForm.get('serialNumber')?.setValue(upper, {emitEvent: false});
        }
    }

    onSubmit() {
        if (!this.bridgeForm.valid) {
            validateAllFormFields(this.bridgeForm);
            return;
        }

        const payload = {
            organization_id: Number(this.organizationInfo['orgId']),
            bridge_name: this.bridgeForm.get('bridgeName')?.value,
            bridge_esn: this.bridgeForm.get('bridgeEsn')?.value,
            serial_number: this.bridgeForm.get('serialNumber')?.value,
            bridge_type: this.bridgeForm.get('bridgeType')?.value
        };

        this.apiService.addBridge(payload).subscribe({
            next: (response: any) => {
                this.generatedRawKey = response?.initial_credential?.raw_key ?? '';
                this.showSuccessModal = !!this.generatedRawKey;
                if (!this.showSuccessModal) {
                    this.toastr.success('New Bridge Added Successfully', 'Success');
                    this.navigateToList();
                }
            },
            error: (err) => this.handleFormErrors(err)
        });
    }

    closeSuccessModal() {
        this.showSuccessModal = false;
        this.generatedRawKey = '';
        this.navigateToList();
    }

    navigateToList() {
        this.router.navigate(['view-bridges'], {
            queryParams: {id: this.organizationInfo['orgId'], name: this.organizationInfo['orgName']}
        });
    }

    handleFormErrors(err: any) {
        const errorBody = err['error'] || {};
        const fieldMap: Record<string, string> = {
            bridge_name: 'Bridge Name',
            bridge_type: 'Bridge Type',
            organization_id: 'Organization',
            bridge_esn: 'Bridge ESN',
            serial_number: 'Serial Number'
        };

        Object.keys(fieldMap).forEach(field => {
            if (errorBody[field]) {
                errorBody[field].forEach((message: string) => {
                    this.toastr.error(message, fieldMap[field]);
                });
            }
        });

        if (errorBody['non_field_errors']) {
            errorBody['non_field_errors'].forEach((message: string) => {
                this.toastr.error(message, 'Error');
            });
        }

        if (
            !errorBody['bridge_name'] &&
            !errorBody['bridge_type'] &&
            !errorBody['organization_id'] &&
            !errorBody['bridge_esn'] &&
            !errorBody['serial_number'] &&
            !errorBody['non_field_errors'] &&
            err['status'] !== 403 &&
            err.status !== 0
        ) {
            this.toastr.error('Something went wrong!', 'Error');
        }
    }

    onCancel() {
        this.navigateToList();
    }
}
